import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import type {
  AnalysisStatus,
  AssessmentStatus,
  Criticality,
  EntityStatus,
  PrefillStatus,
  RiskLevel,
  SchemaStatus,
  SchemaVersionStatus,
  ShareStatus,
  VendorAssessmentStatus,
  VendorStatus,
} from "@/types";

// ─────────────────────────────────────────────
// StatusBadge
//
// Renders a pill-style badge for any KIBORG status enum.
// Colors are driven entirely by CSS tokens from index.css.
// Labels are stored as translation keys and resolved at render time.
// ─────────────────────────────────────────────

type BadgeConfig = {
  bg: string;
  text: string;
  labelKey: string;
};

// ── Assessment Status ────────────────────────
const assessmentStatusConfig: Record<AssessmentStatus, BadgeConfig> = {
  DRAFT: {
    bg: "bg-hover",
    text: "text-text-muted",
    labelKey: "status.draft",
  },
  SUBMITTED: {
    bg: "bg-accent-bg",
    text: "text-accent-text",
    labelKey: "status.submitted",
  },
  IN_REVIEW: {
    bg: "bg-warning-bg",
    text: "text-warning",
    labelKey: "status.inReview",
  },
  APPROVED: {
    bg: "bg-success-bg",
    text: "text-success",
    labelKey: "status.approved",
  },
  REJECTED: {
    bg: "bg-danger-bg",
    text: "text-danger",
    labelKey: "status.rejected",
  },
};

// ── Entity Status ────────────────────────────
const entityStatusConfig: Record<EntityStatus, BadgeConfig> = {
  ACTIVE: {
    bg: "bg-success-bg",
    text: "text-success",
    labelKey: "status.active",
  },
  ARCHIVED: {
    bg: "bg-hover",
    text: "text-text-muted",
    labelKey: "status.archived",
  },
  DECOMMISSIONED: {
    bg: "bg-danger-bg",
    text: "text-danger",
    labelKey: "status.decommissioned",
  },
};

// ── Schema Version Status ────────────────────
const schemaVersionStatusConfig: Record<SchemaVersionStatus, BadgeConfig> = {
  DRAFT: {
    bg: "bg-hover",
    text: "text-text-muted",
    labelKey: "status.draft",
  },
  IN_REVIEW: {
    bg: "bg-warning-bg",
    text: "text-warning",
    labelKey: "status.inReview",
  },
  PUBLISHED: {
    bg: "bg-success-bg",
    text: "text-success",
    labelKey: "status.published",
  },
  DEPRECATED: {
    bg: "bg-hover",
    text: "text-text-muted",
    labelKey: "status.deprecated",
  },
  REJECTED: {
    bg: "bg-danger-bg",
    text: "text-danger",
    labelKey: "status.rejected",
  },
};

// ── Schema Status ────────────────────────────
const schemaStatusConfig: Record<SchemaStatus, BadgeConfig> = {
  DRAFT: {
    bg: "bg-hover",
    text: "text-text-muted",
    labelKey: "status.draft",
  },
  PUBLISHED: {
    bg: "bg-success-bg",
    text: "text-success",
    labelKey: "status.published",
  },
  ARCHIVED: {
    bg: "bg-hover",
    text: "text-text-muted",
    labelKey: "status.archived",
  },
};

// ── Risk Level ───────────────────────────────
const riskLevelConfig: Record<RiskLevel, BadgeConfig> = {
  LOW: {
    bg: "bg-success-bg",
    text: "text-success",
    labelKey: "risk.low",
  },
  MEDIUM: {
    bg: "bg-warning-bg",
    text: "text-warning",
    labelKey: "risk.medium",
  },
  HIGH: {
    bg: "bg-k-orange-bg",
    text: "text-k-orange",
    labelKey: "risk.high",
  },
  VERY_HIGH: {
    bg: "bg-k-purple-bg",
    text: "text-k-purple",
    labelKey: "risk.veryHigh",
  },
  CRITICAL: {
    bg: "bg-danger-bg",
    text: "text-danger",
    labelKey: "risk.critical",
  },
};

// ── Vendor Status ────────────────────────────
const vendorStatusConfig: Record<VendorStatus, BadgeConfig> = {
  ACTIVE: {
    bg: "bg-success-bg",
    text: "text-success",
    labelKey: "status.active",
  },
  INACTIVE: {
    bg: "bg-warning-bg",
    text: "text-warning",
    labelKey: "status.inactive",
  },
  ARCHIVED: {
    bg: "bg-hover",
    text: "text-text-muted",
    labelKey: "status.archived",
  },
};

// ── Vendor Assessment Status ─────────────────
const vendorAssessmentStatusConfig: Record<VendorAssessmentStatus, BadgeConfig> = {
  PENDING: {
    bg: "bg-hover",
    text: "text-text-muted",
    labelKey: "status.pending",
  },
  SENT: {
    bg: "bg-accent-bg",
    text: "text-accent-text",
    labelKey: "status.sent",
  },
  IN_PROGRESS: {
    bg: "bg-warning-bg",
    text: "text-warning",
    labelKey: "status.inProgress",
  },
  UNDER_REVIEW: {
    bg: "bg-accent-bg",
    text: "text-accent-text",
    labelKey: "status.underReview",
  },
  COMPLETED: {
    bg: "bg-success-bg",
    text: "text-success",
    labelKey: "status.completed",
  },
  OVERDUE: {
    bg: "bg-danger-bg",
    text: "text-danger",
    labelKey: "status.overdue",
  },
  REJECTED: {
    bg: "bg-danger-bg",
    text: "text-danger",
    labelKey: "status.rejected",
  },
};

// ── Share Status ─────────────────────────────
const shareStatusConfig: Record<ShareStatus, BadgeConfig> = {
  ACTIVE: {
    bg: "bg-success-bg",
    text: "text-success",
    labelKey: "status.active",
  },
  REVOKED: {
    bg: "bg-danger-bg",
    text: "text-danger",
    labelKey: "status.revoked",
  },
};

// ── Analysis Status ──────────────────────────
const analysisStatusConfig: Record<AnalysisStatus, BadgeConfig> = {
  PENDING: {
    bg: "bg-hover",
    text: "text-text-muted",
    labelKey: "status.pending",
  },
  PROCESSING: {
    bg: "bg-accent-bg",
    text: "text-accent-text",
    labelKey: "status.analysis.PROCESSING",
  },
  COMPLETED: {
    bg: "bg-success-bg",
    text: "text-success",
    labelKey: "status.completed",
  },
  FAILED: {
    bg: "bg-danger-bg",
    text: "text-danger",
    labelKey: "status.analysis.FAILED",
  },
};

// ── Prefill Status ───────────────────────────
const prefillStatusConfig: Record<PrefillStatus, BadgeConfig> = {
  PENDING: {
    bg: "bg-accent-bg",
    text: "text-accent-text",
    labelKey: "status.pending",
  },
  APPROVED: {
    bg: "bg-success-bg",
    text: "text-success",
    labelKey: "status.approved",
  },
  MODIFIED: {
    bg: "bg-warning-bg",
    text: "text-warning",
    labelKey: "status.prefill.MODIFIED",
  },
  REJECTED: {
    bg: "bg-danger-bg",
    text: "text-danger",
    labelKey: "status.rejected",
  },
};

// ── Criticality ──────────────────────────────
const criticalityConfig: Record<Criticality, BadgeConfig> = {
  LOW: {
    bg: "bg-success-bg",
    text: "text-success",
    labelKey: "risk.low",
  },
  MEDIUM: {
    bg: "bg-warning-bg",
    text: "text-warning",
    labelKey: "risk.medium",
  },
  HIGH: {
    bg: "bg-k-orange-bg",
    text: "text-k-orange",
    labelKey: "risk.high",
  },
  CRITICAL: {
    bg: "bg-danger-bg",
    text: "text-danger",
    labelKey: "risk.critical",
  },
};

// ── Discriminated union for type safety ───────
type StatusBadgeProps =
  | { type: "assessment"; status: AssessmentStatus; className?: string }
  | { type: "entity"; status: EntityStatus; className?: string }
  | { type: "schemaVersion"; status: SchemaVersionStatus; className?: string }
  | { type: "schema"; status: SchemaStatus; className?: string }
  | { type: "risk"; status: RiskLevel; className?: string }
  | { type: "vendor"; status: VendorStatus; className?: string }
  | { type: "vendorAssessment"; status: VendorAssessmentStatus; className?: string }
  | { type: "share"; status: ShareStatus; className?: string }
  | { type: "analysis"; status: AnalysisStatus; className?: string }
  | { type: "prefill"; status: PrefillStatus; className?: string }
  | { type: "criticality"; status: Criticality; className?: string };

function getConfig(props: StatusBadgeProps): BadgeConfig {
  switch (props.type) {
    case "assessment":
      return assessmentStatusConfig[props.status];
    case "entity":
      return entityStatusConfig[props.status];
    case "schemaVersion":
      return schemaVersionStatusConfig[props.status];
    case "schema":
      return schemaStatusConfig[props.status];
    case "risk":
      return riskLevelConfig[props.status];
    case "vendor":
      return vendorStatusConfig[props.status];
    case "vendorAssessment":
      return vendorAssessmentStatusConfig[props.status];
    case "share":
      return shareStatusConfig[props.status];
    case "analysis":
      return analysisStatusConfig[props.status];
    case "prefill":
      return prefillStatusConfig[props.status];
    case "criticality":
      return criticalityConfig[props.status];
  }
}

export function StatusBadge(props: StatusBadgeProps) {
  const { t } = useTranslation();
  const config = getConfig(props);
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-badge px-2 py-0.5",
        "text-[12px] font-semibold leading-none",
        config.bg,
        config.text,
        props.className
      )}
    >
      {t(config.labelKey)}
    </span>
  );
}
